import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, Space, Avatar, Image, Spin, Carousel, Button } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FolderOpenOutlined,
  FileImageOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import GopYForm from "../GopYForm";
import { apiCaller } from "src/util/apiCaller";

const { Title, Paragraph, Text } = Typography;

interface HistoricalDocumentDetail {
  [x: string]: any;
  idTulieu: number;
  tenTuLieu: string;
  moTa: string;
  chiTiet: string;
  anhDaiDien: string;
  ngayDang: string;
  ngayDuyetXuatBan: string;
  historicalPeriod: string;
  tacGia: string;
  videos?: string[];
  listTuLieuFile: {
    id: number;
    idTuLieu: number;
    tenFile: string;
    duongDanFile: string;
    moTa: string;
    tacGia: string;
    idNguoiDang: number;
    ngayDang: number;
    isSuDung: boolean;
  }[];
}

const DetailPageGallery: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<HistoricalDocumentDetail | null>(
    null
  );
  const [currentImage, setCurrentImage] = useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchDocumentDetail(id);
    }
  }, [id]);

  const fetchDocumentDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await apiCaller({
        url: `/apipublic/tulieuhienthi/${id}`,
        method: "GET",
      });

      if (response?.data) {
        const doc = response.data;
        const formattedDocument: HistoricalDocumentDetail = {
          idTulieu: doc.idTulieu || 0,
          tenTuLieu: doc.tenTuLieu || "",
          moTa: doc.moTa || "",
          chiTiet: doc.chiTiet || "",
          anhDaiDien: doc.anhDaiDien || "",
          ngayDang: doc.ngayDang || "",
          ngayDuyetXuatBan: doc.ngayDuyetXuatBan || "",
          historicalPeriod: doc.loaiTuLieu.tenLoaiTuLieu || "",
          tacGia: doc.tacGia || "",
          videos: doc.videos || [],
          listTuLieuFile: doc.listTuLieuFile || [],
        };

        setDocument(formattedDocument);
        if (formattedDocument.listTuLieuFile.length > 0) {
          const imageFiles = filterImageFiles(formattedDocument.listTuLieuFile);
          if (imageFiles.length > 0) {
            setCurrentImage(imageFiles[0]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching document detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterImageFiles = (files: any[]) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];
    return files.filter((file) =>
      imageExtensions.some((ext) => file.tenFile.toLowerCase().endsWith(ext))
    );
  };

  const handleImageClick = (image: any) => {
    setCurrentImage(image);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!document) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Title level={3}>Không tìm thấy tài liệu</Title>
      </div>
    );
  }

  const imageFiles = filterImageFiles(document.listTuLieuFile);

  return (
    <>
      <div className="QTI-content-detail">
        <Title level={4}>
          <FileImageOutlined /> {document.tenTuLieu}
        </Title>
        <Space
          direction="vertical"
          size={16}
          style={{ marginBottom: 24 }}
          className="QTI-detail-page-author"
        >
          <Space direction="horizontal" size={0}>
            <UserOutlined />
            <Text strong>{document.tacGia}</Text>
            <Text type="secondary">
              <CalendarOutlined />{" "}
              {new Date(document.ngayDang).toLocaleDateString("vi-VN")}
            </Text>
            <Text type="secondary">
              <FolderOpenOutlined /> {document.historicalPeriod}
            </Text>
          </Space>
        </Space>
        <Paragraph strong style={{ fontSize: 16 }}>
          {document.moTa}
        </Paragraph>
        <div className="QTI-img-center">
          <Image
            src={document.anhDaiDien}
            alt={document.tenTuLieu}
            style={{
              width: "100%",
              maxHeight: 500,
              objectFit: "cover",
              marginBottom: 24,
            }}
          />
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: document.chiTiet }}
          style={{ marginBottom: 24 }}
        />

        <Title level={4}>
          <FileImageOutlined /> Danh sách hình ảnh{" "}
        </Title>
        <Carousel dots={false}>
          {currentImage ? (
            <div
              key={currentImage.id}
              className="QTI-detail-img-main"
              style={{
                cursor: "pointer",
                width: "600px",
                height: "400px",
                margin: "0 auto",
                maxHeight: "600px",
              }}
            >
              <Image
                src={currentImage.duongDanFile}
                alt={currentImage.tenFile}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <div className="slide-overlay">
                <h3 className="slide-title">{currentImage.moTa}</h3>
                <Space
                  direction="horizontal"
                  size={8}
                  style={{
                    position: "absolute",
                    bottom: "16px",
                    left: "16px",
                    background: "rgba(0, 0, 0, 0.6)",
                    borderRadius: "16px",
                    padding: "8px 16px",
                    alignItems: "center",
                    color: "#fff",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  <Text strong style={{ color: "#fff", fontSize: "14px" }}>
                    <UserOutlined /> {currentImage.tacGia}
                  </Text>
                  <Text
                    strong
                    style={{
                      color: "#fff",
                      fontSize: "14px",
                      paddingLeft: "10px",
                    }}
                  >
                    <a href={currentImage.duongDanFile}>
                      <DownloadOutlined /> Tải về{" "}
                    </a>
                  </Text>
                </Space>
              </div>
            </div>
          ) : (
            <p>Không có dữ liệu</p>
          )}
        </Carousel>
        <div
          className="thumbnail-list"
          style={{
            display: "flex",
            justifyContent: "flex-start",
            overflowX: imageFiles.length > 5 ? "auto" : "hidden",
            whiteSpace: "nowrap",
            marginTop: "10px",
            maxHeight: "175px",
          }}
        >
          {imageFiles.map((file) => (
            <div
              key={file.id}
              style={{
                width: "160px",
                height: "120px",
                margin: "0 5px",
                flexShrink: 0,
              }}
            >
              <Image
                preview={false}
                src={file.duongDanFile}
                alt={file.tenFile}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  cursor: "pointer",
                  border:
                    currentImage?.id === file.id ? "2px solid blue" : "none",
                  borderRadius: "8px",
                }}
                onClick={() => {
                  setCurrentImage(file);
                  handleImageClick(file);
                }}
              />
              <a href={file.duongDanFile} className="download-button-overlay">
                Tải về
              </a>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: "10px" }}>
        <GopYForm />
      </div>
    </>
  );
};

export default DetailPageGallery;
